import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { sharePointService } from '../services/sharePointService';
import { useUserSession } from './UserContext';
import authApi from '../services/authApi';

const SharePointContext = createContext();
const STORAGE_KEY = 'securepoint_selected_config';

export const SharePointProvider = ({ children }) => {
  const { userSession } = useUserSession();
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConfig, setSelectedConfig] = useState(() => {
    // Initialize from localStorage if available
    const savedConfig = localStorage.getItem(STORAGE_KEY);
    const configId = savedConfig ? parseInt(savedConfig, 10) : null;
    console.log('Initializing selectedConfig from localStorage:', configId);
    return configId;
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const pendingSelectConfigIdRef = useRef(null);

  // Track selectedConfig changes
  useEffect(() => {
    console.log('selectedConfig changed to:', selectedConfig);
    if (selectedConfig && configurations.length > 0) {
      const config = configurations.find(c => c.id === selectedConfig);
      console.log('Selected config details:', config);
    }
  }, [selectedConfig, configurations]);

  useEffect(() => {
    const fetchConfigurations = async () => {
      setLoading(true);
      try {
        if (userSession?.email) {
          const token = authApi.getToken();
          const response = await sharePointService.getConfig(token);
          
          if (response && response.success && response.all_configurations) {
            console.log('SharePoint configurations loaded in context:', response.all_configurations);
            console.log('Current selectedConfig:', selectedConfig);
            
            // Only set configurations that the user has access to
            const accessibleConfigs = response.all_configurations.filter(config => 
              config.id && config.tenant_url
            );
            
            setConfigurations(accessibleConfigs);
            
            // Set the first accessible configuration as selected by default if none is selected
            // or if the selected one is not in the list
            if (accessibleConfigs.length > 0) {
              const want = pendingSelectConfigIdRef.current;
              const sessionCfg =
                userSession?.impersonatedConfigId != null &&
                !Number.isNaN(parseInt(userSession.impersonatedConfigId, 10))
                  ? parseInt(userSession.impersonatedConfigId, 10)
                  : null;

              if (want != null && accessibleConfigs.some((c) => c.id === want)) {
                setSelectedConfig(want);
                localStorage.setItem(STORAGE_KEY, String(want));
                pendingSelectConfigIdRef.current = null;
              } else if (
                sessionCfg != null &&
                accessibleConfigs.some((c) => c.id === sessionCfg)
              ) {
                setSelectedConfig(sessionCfg);
                localStorage.setItem(STORAGE_KEY, String(sessionCfg));
                pendingSelectConfigIdRef.current = null;
              } else {
                pendingSelectConfigIdRef.current = null;
                const configExists = accessibleConfigs.some(
                  config => config.id === selectedConfig
                );

                if (!selectedConfig || !configExists) {
                  const newSelectedConfig = accessibleConfigs[0].id;
                  console.log('Setting new selectedConfig to:', newSelectedConfig, 'from config:', accessibleConfigs[0]);
                  setSelectedConfig(newSelectedConfig);
                  localStorage.setItem(STORAGE_KEY, String(newSelectedConfig));
                } else {
                  console.log('Keeping existing selectedConfig:', selectedConfig);
                  console.log('Selected config details:', accessibleConfigs.find(c => c.id === selectedConfig));
                }
              }
            } else {
              // If no accessible configurations, clear the selected config
              setSelectedConfig(null);
              localStorage.removeItem(STORAGE_KEY);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching SharePoint configurations:', error);
        setConfigurations([]);
        setSelectedConfig(null);
        localStorage.removeItem(STORAGE_KEY);
      } finally {
        setLoading(false);
      }
    };

    if (userSession?.email) {
      fetchConfigurations();
    } else {
      setLoading(false);
      setConfigurations([]);
      setSelectedConfig(null);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [userSession?.email, userSession?.effectiveEmail, userSession?.impersonatedConfigId, refreshTrigger]);

  const changeSelectedConfig = (configId) => {
    console.log(`changeSelectedConfig called with configId: ${configId}, current selectedConfig: ${selectedConfig}`);
    console.log('Available configurations:', configurations.map(c => ({ id: c.id, tenant_url: c.tenant_url })));
    
    if (configId !== selectedConfig) {
      // Verify the config exists in accessible configurations
      const configExists = configurations.some(config => config.id === configId);
      console.log(`Config ${configId} exists in accessible configs:`, configExists);
      
      if (configExists) {
        console.log(`Changing selected config from ${selectedConfig} to ${configId}`);
        setSelectedConfig(configId);
        localStorage.setItem(STORAGE_KEY, String(configId));
        // Increment refresh trigger to force components to refetch data
        setRefreshTrigger(prev => prev + 1);
        // Force a page reload to ensure all components refresh with the new configuration
        console.log('Reloading page with new configuration...');
        window.location.reload();
      } else {
        console.warn(`Attempted to select inaccessible configuration ID: ${configId}`);
        console.warn('Available config IDs:', configurations.map(c => c.id));
      }
    } else {
      console.log('Config ID is the same as current selected config, no change needed');
    }
  };

  // Get the currently selected configuration object
  const getSelectedConfigObject = () => {
    return configurations.find(config => config.id === selectedConfig) || null;
  };

  const bumpConfigRefresh = (selectConfigIdAfterLoad) => {
    if (selectConfigIdAfterLoad != null) {
      const n = parseInt(selectConfigIdAfterLoad, 10);
      if (!Number.isNaN(n) && n > 0) {
        pendingSelectConfigIdRef.current = n;
      }
    }
    setRefreshTrigger((p) => p + 1);
  };

  return (
    <SharePointContext.Provider 
      value={{ 
        configurations, 
        loading, 
        selectedConfig, 
        changeSelectedConfig,
        selectedConfigObject: getSelectedConfigObject(),
        refreshTrigger,
        bumpConfigRefresh
      }}
    >
      {children}
    </SharePointContext.Provider>
  );
};

export const useSharePoint = () => {
  const context = useContext(SharePointContext);
  if (!context) {
    throw new Error('useSharePoint must be used within a SharePointProvider');
  }
  return context;
}; 